<template>
<div>

    <m-edit-text
        label="Organization Label (i.e. your company's name)"
        :value="profile.organization_account.label"
        :read_only="!can_write"
        v-on:input="setOrganizationName($event)"
    />

    <m-edit-text
        label="Consent Label"
        :value="profile.organization_account.override_consent_organization_label == null ? profile.organization_account.label : profile.organization_account.override_consent_organization_label"
        :read_only="!can_write"
        v-on:input="setConsentLabel($event)"
        show_hint_everywhere
        hint="Shows in the widget and in messages for consent"
        style="margin-bottom: 10px"
    />

    <m-display-text
        label="Organization Id"
        v-model="profile.organization_id"
        disabled
    />

    <m-restricted-field
        v-if="isSystemsAdmin()"
    >
        <m-edit-select-enum
            label="Industry"
            :value="profile.organization_account.industry"
            enum_name="industry_code"
            dialog_title="Select industry"
            v-on:input="setIndustry($event)"
        />
    </m-restricted-field>

    <m-edit-select
            label="Primary Time Zone"
            :value="getCurrentTimezone()"
            :possible_values="getTimezones()"
            :read_only="!can_write"
            v-on:input="setOrganizationTimeZone($event)"
        />

    <m-autocomplete-country-and-subdivision
        :value="country_and_subdivision_wrapper"
        v-on:input="setOrganizationCountryAndSubdivision($event)"
    />

    <v-text-field
        label="Attachment Confirmation"
        :value="getAttachmentConfirmationLabel()"
        disabled
        autocomplete="off"
    >
        <span slot="append-outer">
             <m-edit-menu
                 v-on:menu-action="showAttachmentConfirmationDialog()"
             />
        </span>
    </v-text-field>

    <m-edit-select
        label="Two Factor Authentication (2FA) Requirements"
        v-model="profile.organization_account.two_factor_auth_requirement"
        :possible_values="$rc.enumChoices('organization_two_factor_auth_requirement', false)"
        style="margin-top: 15px"
        v-on:input="onChangeOrganization"
        :return-object="false"
    />

    <m-edit-select
        label="Message and Conversation Privacy Mode"
        v-model="profile.organization_account.features.message_privacy_mode"
        :possible_values="message_privacy_mode_items"
        :read_only="!can_write"
        v-on:input="onClickToChangeFeature"
        style="margin-top: 15px;"
        :return-object="false"
    />

    <v-switch
      v-if="can_write"
      label="Show Message Type Indicator"
      v-on:change="onClickToChangeFeature"
      v-model="profile.organization_account.features.show_message_type_indicator"
    />

    <v-switch
      v-if="can_write"
      label="Use hyperlinks in bot messages to team members (new conversation, new messages, etc.)"
      v-on:change="onClickToChangeFeature"
      v-model="profile.organization_account.features.allow_links_in_bot_messages"
    />

    <m-restricted-field
      :tooltip_text="getPromotionalConsentTooltip()"
      position="checkbox"
    >
      <v-switch
        label="Only allow promotional messages to be sent to recipients with promotional consent on file in RedChirp"
        v-on:change="onClickToChangeFeature"
        v-model="profile.organization_account.features.only_allow_promotional_messages_with_consent"
        :disabled="!($rc.$state.isAuthActorSystemAdmin() || can_write)"
      />
    </m-restricted-field>

    <m-settings-organization-dialog-attachment-confirmation
        ref="dialog_attachment_confirmation"
    />

    <div
        v-if="profile.organization_account.features.compliance_finra"
    >
        <p><b>FINRA Archival Compliance is turned on</b></p>
        <p>This can only be modified on the provision screen by a RedChirp system administrator.  If you need help, please contact us.</p>
        <p>A log of all communciations will be sent to these email addresses:<br/></p>

        <v-chip
            v-for="(item, index) in profile.organization_account.features.compliance_finra_email_addresses_parsed"
            :key="index"
            style="margin-right: 15px;"
        >
            {{item}}
        </v-chip>
    </div>

    <!-- Communication Settings Section -->
    <v-card class="mt-6 mb-6">
        <v-card-title>Communication Settings</v-card-title>
        <v-card-text>
            <v-row>
                <v-col cols="12" sm="6" md="4">
                    <v-btn
                        block
                        v-on:click="onClickBlockedNumbers"
                    >
                        Blocked (Spam) Numbers...
                    </v-btn>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                    <v-btn
                        block
                        v-on:click="onClickDefaultOptInForm"
                    >
                        Default Messaging Opt-in Form...
                    </v-btn>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                    <v-btn
                        block
                        v-on:click="onClickTollFreeOptInForm"
                    >
                        Toll Free Messaging Opt-in Form...
                    </v-btn>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                    <v-btn
                        block
                        v-on:click="onClickDefaultTextMessagingTermsAndConditionsAddendum"
                    >
                        Text Messaging Terms and Conditions Addenum...
                    </v-btn>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                    <v-btn
                        block
                        v-on:click="onClickDefaultTextMessagingPrivacyPolicyAddendum"
                    >
                        Text Messaging Privacy Policy Addenum...
                    </v-btn>
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>

    <!-- Account Management Section -->
    <v-card class="mb-6">
        <v-card-title>Account Management</v-card-title>
        <v-card-text>
            <v-row>
                <v-col cols="12" sm="6" md="4">
                    <v-btn
                        block
                        v-on:click="onClickWelcomeScreen"
                    >
                        Welcome Screen...
                    </v-btn>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                    <v-btn
                        block
                        v-on:click="onClickAccountChangeLog"
                    >
                        Account Change Log...
                    </v-btn>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                    <v-btn
                        block
                        v-on:click="onClickAccountAccessLog"
                    >
                        Account Access Log...
                    </v-btn>
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>

    <!-- Admin Tools Section (only visible to system admins) -->
    <v-card
        class="mb-6"
        v-if="this.$rc.$state.isAuthActorSystemAdmin()"
    >
        <v-card-title>Admin Tools</v-card-title>
        <v-card-text>
            <v-row>
                <v-col cols="12" sm="6" md="4">
                    <m-restricted-button
                        block
                        v-on:click="onClickLinks"
                    >
                        Links...
                    </m-restricted-button>
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>

</div>
</template>

<script>
const _ = require('lodash');

const TestData = require("@/js/TestData.js");

export default {
    props: {
        profile: {
            type: Object,
            default: function() { return {}; }
        },
    },

    data() {
        return {
            message_privacy_mode_items: [
                { 'text' : 'All users may see messages and conversations on any phone number (default)', 'value' : 'all-users-may-access-all-endpoints' },
                { 'text' : 'Ordinary users may only see messages and conversations on their phone numbers (admins can access any messages)', 'value' : 'ordinary-user-access-limited-to-endpoints-they-are-following' },
            ],
        }
    },

    methods:
    {
        showAttachmentConfirmationDialog()
        {
            this.$refs['dialog_attachment_confirmation'].showDialog(
                this.profile.organization_account.organization_id,
                this.profile.organization_account.features.attachment_confirmation_settings,
                this.onSuccessSavingConfirmationSettings
            );
        },
        onSuccessSavingConfirmationSettings(data)
        {
            this.$emit('reload');
        },
        getAttachmentConfirmationLabel()
        {
            let confirmation_settings = this.profile.organization_account.features.attachment_confirmation_settings;

            if (confirmation_settings.confirm_before_sending_externally)
            {
                return "Confirmation required before sending any attachment";
            }

            return "No confirmation required before sending attachments";
        },
        isSystemsAdmin()
        {
            return this.$rc.$state.isAuthActorSystemAdmin();
        },
        
        getPromotionalConsentTooltip()
        {
            if (this.isSystemsAdmin()) {
                return "Org Admin & Staff Only";
            } 
            return "Org Admin Only";
        },

        getTimezones()
        {
            let ret = TestData.getCommonTimeZones();

            for ( let i = 0; i < ret.length; i++ )
            {
                if ( ret[i].value == this.profile.organization_account.primary_timezone )
                    return ret;
            }

            ret.push({
                'text' : this.profile.organization_account.primary_timezone,
                'value' : this.profile.organization_account.primary_timezone,
            });

            return ret;
        },

        getCurrentTimezone()
        {
            let all_zones = this.getTimezones();

            for ( let i = 0; i < all_zones.length; i++ )
            {
                if ( all_zones[i].value == this.profile.organization_account.primary_timezone )
                    return all_zones[i];
            }

            return null;
        },

        setOrganizationName(value)
        {
            let new_organization_acount = _.cloneDeep(this.profile.organization_account);
            new_organization_acount.label = value;

            this.$emit('upsert-organization-account',new_organization_acount);
        },

        setConsentLabel(value)
        {
            let new_organization_acount = _.cloneDeep(this.profile.organization_account);
            new_organization_acount.override_consent_organization_label = value;

            this.$emit('upsert-organization-account',new_organization_acount);
        },

        setIndustry(value)
        {
            let new_organization_account = _.cloneDeep(this.profile.organization_account);
            new_organization_account.industry = value;

            this.$emit('upsert-organization-account',new_organization_account);
        },

        onClickToChangeFeature()
        {
            let patch = {
                'organization_id' : this.profile.organization_account.organization_id,
                'alarm_widget_not_shown_yesterday_or_today' : this.profile.organization_account.features.alarm_widget_not_shown_yesterday_or_today,
                'compliance_finra' : this.profile.organization_account.features.compliance_finra,
                'compliance_finra_email_addresses' : this.profile.organization_account.features.compliance_finra_email_addresses,
                'message_privacy_mode' : this.profile.organization_account.features.message_privacy_mode,
                'show_message_type_indicator' : this.profile.organization_account.features.show_message_type_indicator,
                'allow_links_in_bot_messages': this.profile.organization_account.features.allow_links_in_bot_messages,
                'only_allow_promotional_messages_with_consent': this.profile.organization_account.features.only_allow_promotional_messages_with_consent,
            };

            this.$rc.showSpinner();

            this.$rc.patchOrganizationFeatures(
                patch,

                (data) => {
                    this.$rc.hideSpinner();
                    this.$emit('reload');
                },

                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error','Unable to complete API call');
                },
            );
        },

        onChangeOrganization()
        {
            let new_organization_acount = _.cloneDeep(this.profile.organization_account);
            this.$emit('upsert-organization-account',new_organization_acount);
        },

        setOrganizationTimeZone(zone)
        {
            this.$rc.confirm({
                'title': 'Confirm Change Organization Timezone to '+zone.value,
                'body': 'Are you certain that you would like to change your organization\'s timezone to '+zone.value+'?<br/><br/>This will affect <b>all users</b>, change the times sent on automated messages etc.<br/><br/>Ordinarily an organization\'s timezone is set once during account setup and never changed (unless you have relocated to a new timezone).',
                'btn_text_ok': 'Change',
                'btn_text_cancel': 'Cancel',
                'on_ok': () => {
                    let new_organization_acount = _.cloneDeep(this.profile.organization_account);
                    new_organization_acount.primary_timezone = zone.value;

                    this.$emit('upsert-organization-account',new_organization_acount);
                },
            });
        },

        setOrganizationCountryAndSubdivision(country_and_subdivision)
        {
            let new_organization_acount = _.cloneDeep(this.profile.organization_account);
            new_organization_acount.country_and_subdivision = country_and_subdivision;

            this.$emit('upsert-organization-account',new_organization_acount);
        },

        onClickBlockedNumbers()
        {
            this.$router.push({
                'name': 'settings-organization-spam-numbers',
                'params' : {
                    'organization_id' : this.profile.organization_id,
                }
            });
        },

        onClickDefaultOptInForm()
        {
            let url = this.$router.resolve({
                'name': 'default-opt-in-form',
                'params' : {
                    'organization_id' : this.profile.organization_id,
                }
            });

            window.open(url.href,"_blank");
        },

        onClickTollFreeOptInForm()
        {
            let url = this.$router.resolve({
                'name': 'toll-free-opt-in-form',
                'params' : {
                    'organization_id' : this.profile.organization_id,
                }
            });

            window.open(url.href,"_blank");
        },

        onClickDefaultTextMessagingTermsAndConditionsAddendum()
        {
            let url = this.$router.resolve({
                'name': 'default-terms-and-conditions-addendum',
                'params' : {
                    'organization_id' : this.profile.organization_id,
                }
            });

            window.open(url.href,"_blank");
        },

        onClickDefaultTextMessagingPrivacyPolicyAddendum()
        {
            let url = this.$router.resolve({
                'name': 'default-privacy-policy-addendum',
                'params' : {
                    'organization_id' : this.profile.organization_id,
                }
            });

            window.open(url.href,"_blank");
        },

        onClickWelcomeScreen()
        {
            this.$router.push({
                'name': 'welcome_ordinary_user',
                'params' : {
                    'employee_id': this.$rc.$state.getEmployeeId(),
                }
            });
        },

        onClickAccountChangeLog()
        {
            this.$router.push({
                'name': 'account-change-log',
                'params' : {
                    'organization_id': this.profile.organization_account.organization_id,
                }
            });
        },

        onClickAccountAccessLog()
        {
            this.$router.push({
                'name': 'account-access-log',
                'params' : {
                    'organization_id': this.profile.organization_account.organization_id,
                }
            });
        },

        onClickLinks()
        {
            this.$router.push({
                'name': 'search-links',
                'params' : {
                    'organization_id': this.profile.organization_account.organization_id,
                }
            });
        },
    },

    computed: {
        can_write() {
            return this.profile.actor_permissions.can_write;
        },
        country_and_subdivision_wrapper() {
            return {
                'text' : this.profile.organization_account.country_and_subdivision,
                'value' : this.profile.organization_account.country_and_subdivision,
            };
        },
    },

    mounted () {
        if (this.country_and_subdivision_wrapper == null) return;

        this.$rc.searchCountryAndSubdivisionFullText(
            {
                'search_country_codes' : [],
                'full_text' : this.country_and_subdivision_wrapper.value,
            },

            (data) => {
                this.country_and_subdivision_wrapper.text = data.results[0].text;
            }
        );
    },
}
</script>

<style lang="scss" scoped>

</style>